import { gql } from '@apollo/client';
import client from 'apollo/client';

/**
 * List available exercises for current user
 * @return {Promise}
 */
const list = () =>
  client.query({
    query: gql`
      query availableExercises {
        availableExercises {
          id
          name
          name_en
          slug
          description
          localized_name
          default_duration_minutes
          is_custom
          is_otrixverse
          is_play
          muscle_group
          location
          exercise_type
          image_url_1
          image_url_2
          parameter_types
          parameters {
            id
            parameter_type
            is_required
            unit
          }
        }
      }
    `,
    fetchPolicy: 'network-only'
  });

/**
 * Get exercise by ID
 * @param {string} id
 * @return {Promise}
 */
const find = (id) =>
  client.query({
    query: gql`
      query exercise($id: ID!) {
        exercise(id: $id) {
          id
          name
          name_en
          slug
          description
          description_en
          default_duration_minutes
          parameter_types
          parameters {
            id
            parameter_type
            is_required
            unit
          }
          is_otrixverse
          benchmark_target
          benchmark_metric
          points_multiplier
          muscle_group
          location
          exercise_type
          instructions
          instructions_en
          suggested_sets
          suggested_reps
          image_url_1
          image_url_2
          is_play
        }
      }
    `,
    variables: { id },
    fetchPolicy: 'network-only'
  });

/**
 * Create exercise (admin only)
 * @param {Object} payload
 * @return {Promise}
 */
const create = (payload) =>
  client.mutate({
    mutation: gql`
      mutation createExercise($input: CreateExerciseInput!) {
        createExercise(input: $input) {
          id
          name
          slug
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * Update exercise (admin only)
 * @param {Object} payload
 * @return {Promise}
 */
const update = (payload) =>
  client.mutate({
    mutation: gql`
      mutation updateExercise($id: ID!, $input: UpdateExerciseInput!) {
        updateExercise(id: $id, input: $input) {
          id
          name
          slug
        }
      }
    `,
    variables: {
      id: payload.id,
      input: payload.input
    }
  });

/**
 * Delete exercise (admin only)
 * @param {Array} ids
 * @return {Promise}
 */
const remove = (ids) =>
  client.mutate({
    mutation: gql`
      mutation deleteExercise($id: ID!) {
        deleteExercise(id: $id) {
          id
        }
      }
    `,
    variables: { id: ids[0] }
  });

/**
 * Request new exercise (trainer)
 * @param {Object} payload
 * @return {Promise}
 */
const requestExercise = (payload) =>
  client.mutate({
    mutation: gql`
      mutation requestExercise($input: RequestExerciseInput!) {
        requestExercise(input: $input) {
          id
          name
          status
        }
      }
    `,
    variables: { input: payload }
  });

/**
 * List exercise requests (admin)
 * @param {string} status - PENDING, APPROVED, REJECTED
 * @return {Promise}
 */
const listRequests = (status = 'PENDING') =>
  client.query({
    query: gql`
      query exerciseRequests($status: ExerciseRequestStatus, $first: Int!) {
        exerciseRequests(status: $status, first: $first) {
          data {
            id
            name
            name_en
            description
            default_duration_minutes
            parameters
            status
            rejection_reason
            requester {
              id
              first_name
              last_name
            }
            reviewer {
              id
              first_name
              last_name
            }
            reviewed_at
            created_at
          }
        }
      }
    `,
    variables: { status, first: 50 },
    fetchPolicy: 'network-only'
  });

/**
 * List my exercise requests (trainer)
 * @param {string} status - PENDING, APPROVED, REJECTED
 * @return {Promise}
 */
const listMyRequests = (status = null) =>
  client.query({
    query: gql`
      query myExerciseRequests($status: ExerciseRequestStatus) {
        myExerciseRequests(status: $status) {
          id
          name
          name_en
          description
          default_duration_minutes
          parameters
          status
          rejection_reason
          reviewer {
            id
            first_name
            last_name
          }
          reviewed_at
          created_at
        }
      }
    `,
    variables: status ? { status } : {},
    fetchPolicy: 'network-only'
  });

/**
 * Approve exercise request (admin)
 * @param {string} id
 * @return {Promise}
 */
const approveRequest = (id) =>
  client.mutate({
    mutation: gql`
      mutation approveExerciseRequest($id: ID!) {
        approveExerciseRequest(id: $id) {
          id
          status
          created_exercise_id
        }
      }
    `,
    variables: { id }
  });

/**
 * Reject exercise request (admin)
 * @param {string} id
 * @param {string} reason
 * @param {string} suggestedExerciseId
 * @return {Promise}
 */
const rejectRequest = (id, reason, suggestedExerciseId = null) =>
  client.mutate({
    mutation: gql`
      mutation rejectExerciseRequest($id: ID!, $rejection_reason: String!, $suggested_exercise_id: ID) {
        rejectExerciseRequest(id: $id, rejection_reason: $rejection_reason, suggested_exercise_id: $suggested_exercise_id) {
          id
          status
          rejection_reason
        }
      }
    `,
    variables: { id, rejection_reason: reason, suggested_exercise_id: suggestedExerciseId }
  });

/**
 * Delete exercise request (admin)
 * @param {string} id
 * @return {Promise}
 */
const deleteRequest = (id) =>
  client.mutate({
    mutation: gql`
      mutation deleteExerciseRequest($id: ID!) {
        deleteExerciseRequest(id: $id) {
          id
        }
      }
    `,
    variables: { id }
  });

export default {
  list,
  find,
  create,
  update,
  remove,
  requestExercise,
  listRequests,
  listMyRequests,
  approveRequest,
  rejectRequest,
  deleteRequest
};
